import React from 'react';
import { X, Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const PrintOperationsModal = ({
  isOpen,
  onClose,
  operations = [],
  stats = {},
  selectedDate,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handlePrint = () => {
    // Add print styles dynamically
    const printStyles = `
      <style>
        @media print {
          @page {
            size: A4;
            margin: 10mm;
          }
          
          body * {
            visibility: hidden;
          }
          
          .print-area, .print-area * {
            visibility: visible;
          }
          
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          
          .no-print {
            display: none !important;
          }
          
          table {
            page-break-inside: auto;
            width: 100% !important;
          }
          
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          thead {
            display: table-header-group;
          }
          
          .print-section {
            page-break-inside: avoid;
            margin-bottom: 20px;
          }
          
          .print-table {
            font-size: 12px !important;
          }
          
          .print-table td, .print-table th {
            padding: 8px 4px !important;
            border: 1px solid #000 !important;
          }
        }
      </style>
    `;

    // Inject styles into head
    const existingStyles = document.head.querySelector('#print-styles');
    if (existingStyles) {
      existingStyles.remove();
    }

    const styleElement = document.createElement('div');
    styleElement.id = 'print-styles';
    styleElement.innerHTML = printStyles;
    document.head.appendChild(styleElement);

    // Print after a small delay to ensure styles are applied
    window.print();
  };

  return (
    <div dir='rtl' className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">
            {t('printDailyOperations')}
          </h2>
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <Printer size={16} />
              <span>{t('print')}</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors duration-200"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="print:block print-area">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                {t('dailyOperationsReport')}
              </h1>
              <p className="text-slate-600">
                {new Date(selectedDate).toLocaleDateString(t('dir') === 'rtl' ? 'ar-EG' : 'en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>

            {/* Statistics Summary */}
            <div className="mb-6 p-4 bg-slate-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-3">
                {t('statisticsSummary')}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium">{t('totalCheckIns')}:</span>
                  <span className="ms-2">{stats.totalCheckIns || 0}</span>
                </div>
                <div>
                  <span className="font-medium">{t('totalCheckOuts')}:</span>
                  <span className="ms-2">{stats.totalCheckOuts || 0}</span>
                </div>
                <div>
                  <span className="font-medium">{t('pendingOperations')}:</span>
                  <span className="ms-2">{(stats.pendingCheckIns || 0) + (stats.pendingCheckOuts || 0)}</span>
                </div>
                <div>
                  <span className="font-medium">{t('additionalRevenue')}:</span>
                  <span className="ms-2">{stats.additionalRevenue || 0} {t('EGP')}</span>
                </div>
              </div>
            </div>

            {/* Operations List */}
            <div className="space-y-6">
              {/* Check-ins */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-green-700">
                  {t('checkIns')}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse border border-slate-300">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-300 p-2 w-[30px]">
                          {t('Time')}
                        </th>
                        <th className="border border-slate-300 p-2 w-[180px]">
                          {t('guest')}
                        </th>
                        <th className="border border-slate-300 p-2 w-[80px]">
                          {t('unit')}
                        </th>
                        <th className="border border-slate-300 p-2 w-[120px]">
                          {t('Status')}
                        </th>
                        <th className="border border-slate-300 p-2">
                          {t('Notes')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {operations
                        .filter(op => op.type === 'checkin')
                        .map(operation => (
                          <tr key={operation.id}>
                            <td className="border border-slate-300 p-2">
                              {operation.expectedTime}
                            </td>
                            <td className="border border-slate-300 p-2">
                              {operation.reqClientName}
                            </td>
                            <td className="border border-slate-300 p-2">
                              {operation.unitCode}
                            </td>
                            <td className="border border-slate-300 p-2">
                              <span className={`px-2 py-1 rounded text-xs ${operation.operationStatus === 'confirmed' ? 'bg-green-100 text-green-800' :
                                operation.operationStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  operation.operationStatus === 'early' ? 'bg-blue-100 text-blue-800' :
                                    operation.operationStatus === 'late' ? 'bg-orange-100 text-orange-800' :
                                      'bg-red-100 text-red-800'
                                }`}>
                                {t(operation.operationStatus)}
                              </span>
                            </td>
                            <td className="border border-slate-300 p-2">
                              {/* {operation.notes || '-'} */}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Check-outs */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-orange-700">
                  {t('checkOuts')}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse border border-slate-300">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-300 p-2 w-[30px]">
                          {t('Time')}
                        </th>
                        <th className="border border-slate-300 p-2 w-[180px]">
                          {t('guest')}
                        </th>
                        <th className="border border-slate-300 p-2 w-[80px]">
                          {t('unit')}
                        </th>
                        <th className="border border-slate-300 p-2 w-[120px]">
                          {t('Status')}
                        </th>
                        <th className="border border-slate-300 p-2">
                          {t('Notes')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {operations
                        .filter(op => op.type === 'checkout')
                        .map(operation => (
                          <tr key={operation.id}>
                            <td className="border border-slate-300 p-2">
                              {operation.expectedTime}
                            </td>
                            <td className="border border-slate-300 p-2">
                              {operation.reqClientName}
                            </td>
                            <td className="border border-slate-300 p-2">
                              {operation.unitCode}
                            </td>
                            <td className="border border-slate-300 p-2">
                              <span className={`px-2 py-1 rounded text-xs ${operation.operationStatus === 'confirmed' ? 'bg-green-100 text-green-800' :
                                operation.operationStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  operation.operationStatus === 'early' ? 'bg-blue-100 text-blue-800' :
                                    operation.operationStatus === 'late' ? 'bg-orange-100 text-orange-800' :
                                      'bg-red-100 text-red-800'
                                }`}>
                                {t(operation.operationStatus)}
                              </span>
                            </td>
                            <td className="border border-slate-300 p-2">
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};